<template>
  <div v-if="isLoggedIn && user" class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">Update Profile</p>
    </header>
    <section class="modal-card-body">
      <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
        <div>
          <div>
            <h2 class="title is-5 is-inline-block">Picture</h2>
          </div>

          <div>
            <figure class="image avatar-update is-128x128">
              <b-button
                @click="isAvatarUploadModalActive = true"
                class="overlay edit"
                size="is-small"
                icon-left="pencil"
              ></b-button>

              <img
                class="is-rounded"
                :src="user.avatar || currentUser.attributes.avatar.url"
              />
            </figure>
          </div>
        </div>

        <b-field class="pb-3">
          <ValidationProvider
            rules="required|between:3,30"
            name="Title"
            v-slot="{ errors, valid }"
          >
            <b-field
              :type="{ 'is-danger': errors[0], 'is-success': valid }"
              :message="errors"
            >
              <template slot="label">
                Cosplayer Name
                <b-tooltip
                  position="is-right"
                  type="is-dark"
                  label="Cosplayer name displayed on your profile"
                >
                  <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                </b-tooltip>
              </template>
              <b-input
                maxlength="30"
                v-model="user.cosplayer_name"
                placeholder="ABC Cosplay"
              ></b-input>
            </b-field>
          </ValidationProvider>
        </b-field>

        <b-field>
          <template slot="label">
            Motto
            <b-tooltip
              position="is-right"
              type="is-dark"
              label="Small motivational sentence which represents you"
            >
              <b-icon size="is-small" icon="help-circle-outline"></b-icon>
            </b-tooltip>
          </template>
          <b-input
            v-model="user.motto"
            maxlength="100"
            value="C'est le commencement d'un nouveau printemps"
            placeholder="Be yourself. Everyone else is already taken."
          ></b-input>
        </b-field>

        <div class="pb-2">
          <span class="has-text-weight-semibold is-size-6">Social Networks </span>
          <b-tooltip
            position="is-right"
            type="is-dark"
            label="All the pages you own as a cosplayer"
          >
            <b-icon size="is-small" icon="help-circle-outline"></b-icon>
          </b-tooltip>
        </div>

        <b-field>
          <p class="control"><span class="button is-static">facebook.com/</span></p>
          <b-input
            v-model="user.facebook"
            expanded
            maxlength="100"
            type="text"
            name="facebook"
            autocomplete="facebook"
          ></b-input>
        </b-field>

        <b-field>
          <p class="control"><span class="button is-static">instagram.com/</span></p>
          <b-input
            v-model="user.instagram"
            expanded
            type="text"
            maxlength="100"
            name="instagram"
            autocomplete="instagram"
          ></b-input>
        </b-field>
        <b-field>
          <p class="control"><span class="button is-static">twitter.com/</span></p>
          <b-input
            v-model="user.twitter"
            expanded
            maxlength="100"
            type="text"
            name="twitter"
            autocomplete="twitter"
          ></b-input>
        </b-field>
        <b-field>
          <p class="control">
            <span class="button is-static">youtube.com/channel/</span>
          </p>
          <b-input
            v-model="user.youtube"
            expanded
            maxlength="100"
            name="youtube"
            autocomplete="youtube"
          ></b-input>
        </b-field>
        <b-field>
          <p class="control"><span class="button is-static">ko-fi.com/</span></p>
          <b-input
            v-model="user.kofi"
            expanded
            maxlength="100"
            type="text"
            name="ko-fi"
            autocomplete="ko-fi"
          ></b-input>
        </b-field>

        <b-field>
          <country-select
            :selected-country="user.country"
            :display-success="false"
            @countrySelected="user.country = $event"
          ></country-select>
        </b-field>

        <b-field>
          <template slot="label">
            Birth date
            <b-tooltip position="is-right" type="is-dark" label="Day you are born">
              <b-icon size="is-small" icon="help-circle-outline"></b-icon>
            </b-tooltip>
          </template>
          <b-field>
            <b-datepicker
              v-model="user.birthday"
              name="birthday"
              placeholder="Click to select..."
              icon="cake-variant"
            >
            </b-datepicker>
          </b-field>
        </b-field>
        <b-field v-if="user.configuration" grouped>
          <b-switch v-model="user.configuration.birthday" rounded>
            Display your birthday
          </b-switch>

          <b-switch v-model="user.configuration.age" rounded> Display your age </b-switch>
        </b-field>

        <b-field grouped>
          <template slot="label">
            Gender
            <b-tooltip
              position="is-right"
              type="is-dark"
              label="Gender that represent you the best"
            >
              <b-icon size="is-small" icon="help-circle-outline"></b-icon>
            </b-tooltip>
          </template>
          <b-select v-model="user.gender" placeholder="Choose" icon="account">
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="nonbinary">Non-Binary</option>
            <option value="other">Other</option>
          </b-select>

          <b-switch v-if="user.configuration" v-model="user.configuration.gender" rounded>
            Display your gender
          </b-switch>
        </b-field>

        <b-field>
          <template slot="label">
            Language(s)
            <b-tooltip
              position="is-right"
              type="is-dark"
              label="Language(s) in which you can exchange"
            >
              <b-icon size="is-small" icon="help-circle-outline"></b-icon>
            </b-tooltip>
          </template>

          <b-taginput
            v-model="user.language_list"
            :data="filteredLanguages"
            @typing="getFilteredLanguages"
            :allow-new="false"
            autocomplete
            maxtags="20"
            placeholder="Add a language"
          ></b-taginput>
        </b-field>

        <b-field>
          <template slot="label">
            Bio
            <b-tooltip
              position="is-right"
              type="is-dark"
              label="Describe yourself in a few lines"
            >
              <b-icon size="is-small" icon="help-circle-outline"></b-icon>
            </b-tooltip>
          </template>

          <b-input
            v-model="user.description"
            type="textarea"
            name="description"
            maxlength="1000"
            autocomplete="description"
          ></b-input>
        </b-field>
      </ValidationObserver>
    </section>

    <footer class="modal-card-foot">
      <span class="card-footer-item">
        <b-button type="" @click="cancelUpdate">Close</b-button>
      </span>
      <span class="card-footer-item">
        <b-button type="is-success" @click="confirmUpdate">Update</b-button>
      </span>
    </footer>

    <b-modal
      :active.sync="isAvatarUploadModalActive"
      has-modal-card
      trap-focus
      aria-role="dialog"
      aria-modal
    >
      <image-uploader
        :emit-signal="'avatarChanged'"
        :title="'avatar'"
        @avatarChanged="user.avatar = $event"
      />
    </b-modal>
  </div>
</template>

<script>
import CountrySelect from '@/components/CountrySelect'
import ImageUploader from '@/components/modals/ImageUploader'
import { mapGetters } from 'vuex'
import { notificationMixin } from '@/mixins/notificationMixin'
import { languagesMixin } from '@/mixins/languagesMixin'

import { ValidationObserver, ValidationProvider } from 'vee-validate'

export default {
  mixins: [notificationMixin, languagesMixin],
  data () {
    return {
      user: null,
      configuration: null,
      filteredLanguages: this.getLanguageNames,
      isAvatarUploadModalActive: false
    }
  },
  computed: {
    ...mapGetters({
      isLoggedIn: 'isLoggedIn',
      currentUser: 'currentUser'
    })
  },
  components: {
    CountrySelect,
    ValidationObserver,
    ValidationProvider,
    ImageUploader
  },
  watch: {
    currentUser (newValue, oldValue) {
      this.user = JSON.parse(JSON.stringify(newValue.attributes))
      this.user.birthday = new Date(this.user.birthday)
      this.user.avatar = null
      this.configuration = this.user.configuration
    }
  },
  mounted () {
    if (this.currentUser !== null && this.currentUser !== undefined) {
      this.user = JSON.parse(JSON.stringify(this.currentUser.attributes))
      this.user.birthday = new Date(this.user.birthday)
      this.user.avatar = null
      this.configuration = this.user.configuration
    }
  },
  methods: {
    confirmUpdate () {
      this.$refs.observer.validate().then((success) => {
        if (success) {
          [
            'configuration',
            'id',
            'username',
            'created_at',
            'age',
            'theme',
            'email',
            'skills'
          ].forEach((e) => delete this.user[e])
          let userPayload = this.user
          userPayload.birthday = userPayload.birthday.toString()
          let configurationPayload = {
            age: this.configuration.age,
            gender: this.configuration.gender,
            birthday: this.configuration.birthday
          }
          this.$store
            .dispatch('users/update', userPayload)
            .then((_) => {
              this.$store
                .dispatch('users/configurations/update', configurationPayload)
                .then((resp) => {
                  this.$parent.close()
                  this.actionSuccess('Update successful !')
                  this.$emit('userUpdated', resp.data.data.attributes)
                })
                .catch((error) => {
                  console.log(error.response)
                  this.alertError()
                })
            })
            .catch((error) => {
              console.log(error.response)
              this.alertError()
            })
        }
      })
    },
    getFilteredLanguages (text) {
      this.filteredLanguages = this.getLanguageNames.filter((option) => {
        return option.toString().toLowerCase().indexOf(text.toLowerCase()) >= 0
      })
    }
  }
}
</script>

<style lang="scss" scoped></style>
